import { Spacer } from "components/views/atoms/Atoms"
import { Body } from "components/views/atoms/Bodies"
import React, { useMemo } from "react"
import L10n from "lib/carrot-l10n"
import { useTrackView } from "../../../../utils/heap"
import styled from "styled-components"
import { Typography } from "@carrotfertility/carotene-core"
import { getPiiProps } from "services/tracking"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { FormattedMessage } from "react-intl"
import { RegistrationButtonFooter, ContinueButton } from "./RegistrationButtons"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const UnorderedPlanLimitsList = styled.ul`
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  margin: 1rem 1.5rem;
  color: #000000cc;
  &:before {
    color: #000000cc;
  }
  &:after {
    color: #000000cc;
  }
)
`
const PlanLimitsListItem = styled.li`
  line-height: 1.5;
  color: #000000cc;
  &:before {
    color: #000000cc;
  }
  &:after {
    color: #000000cc;
  }
`

interface ReimbursementCategoryProps {
  name?: string
  priority?: number
}

interface ReimbursementPlanProps {
  currencySymbol?: string
  hasUnlimitedBenefit?: boolean
  reimbursementPercent?: number
  lifetimeMax?: number
  yearlyMax?: number
}

interface SinglePlanProps {
  reimbursementPlan: ReimbursementPlanProps
  reimbursementCategory: ReimbursementCategoryProps
}

interface MultipleCategoriesProps {
  companyName?: string
  currencySymbol?: string
  companyPlans?: Array<SinglePlanProps>
  supportsOOPM?: boolean
  onBack: () => void
  onComplete: () => void
}

function DisplayMultipleCategoriesVaryingCostShare({
  companyName,
  companyPlans,
  currencySymbol
}: MultipleCategoriesProps): JSX.Element {
  const planHasNoLifetimeMax = companyPlans.some((plan) => plan.reimbursementPlan.lifetimeMax === null)

  return (
    <>
      <Typography {...getPiiProps()}>
        {l`fle2.financialSupport.introText`}
        {` ${companyName} provides up to ${planHasNoLifetimeMax ? `an annual` : `a lifetime`} maximum of:`}
      </Typography>
      <Spacer height={0.5} />
      <UnorderedPlanLimitsList>
        {planHasNoLifetimeMax &&
          companyPlans.map((currVal, iterator) => {
            return (
              <PlanLimitsListItem key={iterator}>
                {currencySymbol +
                  currVal.reimbursementPlan.yearlyMax.toLocaleString("en-US") +
                  ` for ${currVal.reimbursementCategory.name.toLowerCase()}`}
              </PlanLimitsListItem>
            )
          })}
        {!planHasNoLifetimeMax &&
          companyPlans.map((currVal, iterator) => {
            return (
              <PlanLimitsListItem key={iterator}>
                {currencySymbol +
                  currVal.reimbursementPlan?.lifetimeMax.toLocaleString("en-US") +
                  ` for ${currVal.reimbursementCategory.name.toLowerCase()}`}
              </PlanLimitsListItem>
            )
          })}
      </UnorderedPlanLimitsList>
      <Spacer height={0.5} />
      {companyPlans.some((plan) => plan.reimbursementPlan.yearlyMax != plan.reimbursementPlan.lifetimeMax) ? (
        <>
          <Spacer height={0.5} />
          <Body color="black-80">{l`fle2.financialSupport.varyingCostShare.annualLimits`}</Body>
        </>
      ) : (
        <>
          <Spacer height={0.5} />
          <Body color="black-80">{l`fle2.financialSupport.varyingCostShare.noAnnualLimits`}</Body>
        </>
      )}
    </>
  )
}

function DisplayMultipleCategorySameCostShare({
  companyName,
  companyPlans,
  currencySymbol
}: MultipleCategoriesProps): JSX.Element {
  const companyCostShare = companyPlans[0].reimbursementPlan.reimbursementPercent
  const planHasNoLifetimeMax = companyPlans.some((plan) => plan.reimbursementPlan.lifetimeMax === null)
  return (
    <>
      <Typography {...getPiiProps()}>
        {l`fle2.financialSupport.introText`}
        {` ${companyName} will pay for ${companyCostShare}% of your costs, up to ${
          planHasNoLifetimeMax ? `an annual` : `a lifetime`
        } maximum of:`}
        <UnorderedPlanLimitsList>
          {planHasNoLifetimeMax &&
            companyPlans.map((currVal, iterator) => {
              return (
                <PlanLimitsListItem key={iterator}>
                  {currencySymbol +
                    currVal.reimbursementPlan.yearlyMax.toLocaleString("en-US") +
                    ` for ${currVal.reimbursementCategory.name.toLowerCase()}`}
                </PlanLimitsListItem>
              )
            })}
          {!planHasNoLifetimeMax &&
            companyPlans.map((currVal, iterator) => {
              return (
                <PlanLimitsListItem key={iterator}>
                  {currencySymbol +
                    currVal.reimbursementPlan?.lifetimeMax.toLocaleString("en-US") +
                    ` for ${currVal.reimbursementCategory.name.toLowerCase()}`}
                </PlanLimitsListItem>
              )
            })}
        </UnorderedPlanLimitsList>
        {companyPlans.some((plan) => plan.reimbursementPlan.yearlyMax != plan.reimbursementPlan.lifetimeMax) ? (
          <>
            <Spacer height={0.5} />
            <Body color="black-80">{l`fle2.financialSupport.annualLimitsAndNoCostShareDisplay`}</Body>
          </>
        ) : (
          <>
            <Spacer height={0.5} />
            <Body color="black-80">{l`fle2.financialSupport.NoAnnualLimitsAndNoCostShareDisplay`}</Body>
          </>
        )}
      </Typography>
    </>
  )
}

function SingleCategoryDisplay({
  companyName,
  companyPlans,
  currencySymbol,
  supportsOOPM
}: MultipleCategoriesProps): JSX.Element {
  const planHasUnlimited = companyPlans.find((plan) => plan.reimbursementPlan.hasUnlimitedBenefit)
  const reimbursementPlan = planHasUnlimited ? planHasUnlimited?.reimbursementPlan : companyPlans[0].reimbursementPlan
  const planHasNoLifetimeMax = companyPlans.some((plan) => plan.reimbursementPlan.lifetimeMax === null)

  return (
    <Typography {...getPiiProps()}>
      <Spacer height={0.5} />
      {supportsOOPM && (
        <>
          <Body color="black-80">
            Your benefit covers eligible expenses from eligible providers to help make your journey more accessible.{" "}
            {companyName} provides up to a lifetime maximum of {currencySymbol}
            {reimbursementPlan?.lifetimeMax ? reimbursementPlan?.lifetimeMax.toLocaleString() : null}.
          </Body>
          <Spacer height={0.5} />
          <Body color="black-80">{l`fle2.financialSupport.varyingCostShare.noAnnualLimits`}</Body>
        </>
      )}
      {!supportsOOPM && (
        <>
          {l`fle2.financialSupport.introText`}
          <Spacer height={0.5} />
          {reimbursementPlan.hasUnlimitedBenefit
            ? ` ${companyName} covers ${reimbursementPlan.reimbursementPercent}% for the cost of eligible care.`
            : ` ${companyName} will pay for ${reimbursementPlan.reimbursementPercent}% of your costs, up to ${
                planHasNoLifetimeMax
                  ? `an annual maximum of ${currencySymbol}${reimbursementPlan?.yearlyMax.toLocaleString()}.`
                  : `a lifetime maximum of ${currencySymbol}${
                      reimbursementPlan?.lifetimeMax ? reimbursementPlan?.lifetimeMax.toLocaleString() : null
                    }.`
              }`}
          {!reimbursementPlan.hasUnlimitedBenefit ? (
            reimbursementPlan.yearlyMax !== reimbursementPlan.lifetimeMax ? (
              <>
                <Spacer height={0.5} />
                <Body color="black-80">{l`fle2.financialSupport.annualLimitsAndNoCostShareDisplay`}</Body>
              </>
            ) : (
              <>
                <Spacer height={0.5} />
                <Body color="black-80">{l`fle2.financialSupport.NoAnnualLimitsAndNoCostShareDisplay`}</Body>
              </>
            )
          ) : (
            <>
              <Spacer height={0.5} />
              <Body color="black-80">{l`fle2.financialSupport.NoAnnualLimitsAndNoCostShareDisplay`}</Body>
            </>
          )}
        </>
      )}
    </Typography>
  )
}

export default function FinancialSupport({
  companyName,
  companyPlans,
  currencySymbol,
  supportsOOPM,
  onBack,
  onComplete
}: MultipleCategoriesProps): JSX.Element {
  useTrackView("FinancialSupportRegFlow", "Financial support screen")

  const hasMultiplePlans = useMemo(() => companyPlans.length > 1, [companyPlans])

  const hasPlansWithVaryingCostShare = useMemo(() => {
    return companyPlans.some((cv, i, companyPlans) => {
      return cv.reimbursementPlan.reimbursementPercent !== companyPlans[0].reimbursementPlan.reimbursementPercent
    })
  }, [companyPlans])

  const hasAnyPlansWithUnlimitedBenefit = useMemo(() => {
    return companyPlans.some((cv) => {
      return cv.reimbursementPlan.hasUnlimitedBenefit !== false
    })
  }, [companyPlans])

  const companySupportsOOPM = useMemo(() => {
    return supportsOOPM
  }, [supportsOOPM])

  return (
    <>
      <RegistrationFlowTitle title="Get financial support" />
      <RegistrationFlowLayout
        header={"Get financial support"}
        imageName="illo-financial"
        description={
          <>
            {hasAnyPlansWithUnlimitedBenefit && (
              <SingleCategoryDisplay
                onBack={onBack}
                onComplete={onComplete}
                companyName={companyName}
                currencySymbol={currencySymbol}
                companyPlans={companyPlans}
                supportsOOPM={companySupportsOOPM}
              />
            )}
            {hasPlansWithVaryingCostShare && hasMultiplePlans && !hasAnyPlansWithUnlimitedBenefit && (
              <DisplayMultipleCategoriesVaryingCostShare
                onBack={onBack}
                onComplete={onComplete}
                companyPlans={companyPlans}
                companyName={companyName}
                currencySymbol={currencySymbol}
              />
            )}
            {!hasPlansWithVaryingCostShare && hasMultiplePlans && !hasAnyPlansWithUnlimitedBenefit && (
              <DisplayMultipleCategorySameCostShare
                companyName={companyName}
                companyPlans={companyPlans}
                onBack={onBack}
                onComplete={onComplete}
                currencySymbol={currencySymbol}
              />
            )}
            {!hasPlansWithVaryingCostShare && !hasMultiplePlans && !hasAnyPlansWithUnlimitedBenefit && (
              <SingleCategoryDisplay
                onBack={onBack}
                onComplete={onComplete}
                companyName={companyName}
                currencySymbol={currencySymbol}
                companyPlans={companyPlans}
                supportsOOPM={companySupportsOOPM}
              />
            )}
          </>
        }
        interactiveElements={
          <RegistrationButtonFooter
            onBack={onBack}
            continueButton={
              <ContinueButton onClick={onComplete}>
                <FormattedMessage defaultMessage="Continue" />
              </ContinueButton>
            }
          />
        }
      />
    </>
  )
}
